import { useFormikContext } from 'formik';
import { isFunction, get } from 'lodash-es';
import React, { useEffect, useState, useCallback } from 'react';
import * as Yup from 'yup';

import * as events from 'utils/events';
import { scrollToError } from 'utils/helpers';

import { useWizardContext, WizardNavigationButtonProps } from '../context';
import { WizardStepProps } from '../interfaces';
import * as Styled from './styled';

/**
 * Navigation component properties
 */
export type ComponentProps<T> = Pick<
  WizardStepProps<T>,
  'validation' | 'navigation' | 'scrollOnSubmitError'
>;

function Navigation<T>(props: ComponentProps<T>) {
  const {
    navigation = {},
    validation = {
      fieldNames: [],
      schema: Yup.mixed().notRequired()
    },
    scrollOnSubmitError
  } = props;

  // https://github.com/jquense/yup#mixedwhenkeys-string--arraystring-builder-object--value-schema-schema-schema (providing "context" of the who list of values for the validate schema)
  // access the form context
  const formContext = useFormikContext<T>();
  const wizardContext = useWizardContext();
  const [isValid, setIsvalid] = useState(false);

  // Remove extra methods on "FormikContextType" that are not part of the "FormikProps" interface
  delete formContext.validate;
  delete formContext.validationSchema;
  const { values, isSubmitting, isValidating, setFieldTouched, errors } = formContext;

  const { schema } = validation;
  // Execute the Yup schema validation upon change to the values collection or schema definition
  useEffect(() => {
    const runAsync = async () => {
      if (isFunction(schema)) {
        const isValid =
          (await schema(values, wizardContext.appStates).isValid(values, {
            context: values
          })) ?? true;
        setIsvalid(isValid);
      } else {
        const isValid =
          (await schema.isValid(values, {
            context: values
          })) ?? true;
        setIsvalid(isValid);
      }
    };
    runAsync();
  }, [schema, wizardContext.appStates, values]);

  /**
   * Simulate an onClick event
   * @param button button that is been pressed
   */
  const onClick = useCallback(
    (button: WizardNavigationButtonProps<T>) => {
      if (button.shouldValidate) {
        const fieldNames = isFunction(validation.fieldNames)
          ? validation.fieldNames(values, wizardContext.appStates)
          : validation.fieldNames;

        fieldNames.forEach(name => {
          // set touched event and validate all fields together
          setFieldTouched(name, true, true);
        });

        // In case the validation been successul, then trigger the callback event
        if (isValid) {
          button.onClick(
            { dispatch: wizardContext.dispatch },
            formContext,
            wizardContext.appStates
          );
        } else {
          if (button.onSubmitError) {
            const fieldsWithErrors = fieldNames.filter(field => !!get(errors, field));
            button.onSubmitError(formContext, fieldsWithErrors);
          }
          if (scrollOnSubmitError) {
            setTimeout(() => {
              scrollToError();
            }, 50);
          }
        }
      } else {
        button.onClick({ dispatch: wizardContext.dispatch }, formContext, wizardContext.appStates);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      validation,
      values,
      wizardContext.appStates,
      wizardContext.dispatch,
      isValid,
      setFieldTouched,
      formContext,
      scrollOnSubmitError
    ]
  );

  /**
   * event handler to trigger when the user clicks the next/submit button
   * @param event event to trigger upon successful client-side validation
   */
  const onBeforeMouseClick = useCallback(
    (button: WizardNavigationButtonProps<T>) =>
      events.onMouseLeftClick(e => {
        e.preventDefault();
        onClick(button);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClick]
  );

  /**
   * Event handler to trigger when user presses the "Enter" keyboard
   * @param button button to trigger event for
   */
  const onBeforeKeyboardClick = useCallback(
    (button: WizardNavigationButtonProps<T>) =>
      events.onKeyboardEnter(e => {
        e.preventDefault();
        onClick(button);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClick]
  );

  // Parse the navigation props and set default values
  const { normal = [], edit = [], order = 'ascending' } = navigation;

  /**
   * Render <Styled.NavButton /> elements with 2 events "onKeyDown" and "onMouseDown". This is done instead of regular "onClick" to avoid a scenario where user focus was on an <Input /> element and then clicked one of the navigation buttons. Previously the handleChange/handleBlur events of the <Input />
   * element caused the button to lose track of its event, which resulted in user had to click twice on the button in order to trigger an event of dispatching an action to the main <Wizard /> context provider. Instead we using "onKeyUp" and "onMouseDown" events to avoid this scenario from happening and it will always
   * trigger on user mouse left click or hitting the "Enter" keycode on the keyboard (for Screen readers support)
   */
  return (
    <Styled.Container order={order}>
      {/* Edit mode navigation */}
      {wizardContext.mode === 'Edit' ? (
        <React.Fragment>
          {(isFunction(edit) ? edit(wizardContext.appStates) : edit).map((button, i) => {
            return (
              <Styled.NavButton
                key={i}
                buttonType={button.type}
                onKeyDown={onBeforeKeyboardClick(button)}
                onMouseDown={onBeforeMouseClick(button)}
                disabled={isValidating || isSubmitting}>
                {button.content}
              </Styled.NavButton>
            );
          })}
        </React.Fragment>
      ) : (
        <React.Fragment>
          {(isFunction(normal) ? normal(wizardContext.appStates) : normal).map((button, i) => {
            return (
              <Styled.NavButton
                key={i}
                data-testid={`Wizard-NavigationButton-${i}`}
                type={button.isSubmit ? 'submit' : 'button'}
                buttonType={button.type}
                onKeyDown={onBeforeKeyboardClick(button)}
                onMouseDown={onBeforeMouseClick(button)}
                disabled={isValidating || isSubmitting}>
                {button.content}
              </Styled.NavButton>
            );
          })}
        </React.Fragment>
      )}
    </Styled.Container>
  );
}

export default Navigation;
